import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { PrintScheduleDto, PrintScheduleSessionDto } from '@/infrastructure/network/dto/PrintScheduleDto';

dayjs.extend(customParseFormat);

export type SessionFilterType = 'all' | 'morning' | 'afternoon';

export type DatePreset = 'today' | 'thisWeek' | 'fullPeriod';

const parseYmdToLocalStart = (value?: string): Date | undefined => {
	if (!value) return undefined;
	const d = dayjs(value, 'YYYY-MM-DD', true);
	return d.isValid() ? d.startOf('day').toDate() : undefined;
};

// String (already in correct timezone) -> JS Date, no day shift
const parseStringDateToLocalStart = (value?: string): Date | undefined => {
	if (!value) return undefined;
	const d = dayjs(value);
	return d.isValid() ? d.toDate() : undefined; // NOTE: no startOf('day')
};

const isSameCalendarDay = (a: Date, b: Date): boolean => dayjs(a).isSame(b, 'day');

/**
 * Filters schedule data based on session and date selection
 * @param scheduleData - The complete schedule data
 * @param sessionFilter - The session filter type ('all', 'morning', 'afternoon')
 * @param dateFrom - Optional start date filter
 * @param dateTo - Optional end date filter
 * @returns Filtered schedule data
 */
export const filterScheduleBySession = (
	scheduleData: PrintScheduleDto | null,
	sessionFilter: SessionFilterType,
	dateFrom?: Date,
	dateTo?: Date
): PrintScheduleDto | null => {
	if (!scheduleData) return null;

	let filteredDates = scheduleData.dates;

	// Filter by date range if provided
	if (dateFrom || dateTo) {
		const matchExactDay = dateFrom && dateTo && isSameCalendarDay(dateFrom, dateTo);
		filteredDates = filteredDates.filter((date) => {
			// API date as string (e.g., ISO with Z)
			const scheduleDate = parseStringDateToLocalStart(date.date) ?? new Date(NaN);
			if (Number.isNaN(scheduleDate.getTime())) return false;

			if (matchExactDay && dateFrom) {
				return isSameCalendarDay(scheduleDate, dateFrom);
			}
			if (dateFrom && scheduleDate < dateFrom) return false;
			if (dateTo && scheduleDate > dateTo) return false;
			return true;
		});
	}

	// Filter by session if not 'all'
	if (sessionFilter !== 'all') {
		filteredDates = filteredDates.map((date) => {
			// If no sessions exist (useSessions=false), return empty sessions for specific filters
			if (!date.sessions || date.sessions.length === 0) {
				return { ...date, sessions: [] };
			}

			// Filter sessions based on selection
			const filteredSessions: PrintScheduleSessionDto[] = date.sessions.filter((session) => {
				switch (sessionFilter) {
					case 'morning':
						return session.type === 'SESSION_TYPE_MORNING';
					case 'afternoon':
						return session.type === 'SESSION_TYPE_AFTERNOON';
					default:
						return true;
				}
			});

			return {
				...date,
				sessions: filteredSessions
			};
		});
	}

	return {
		...scheduleData,
		dates: filteredDates
	};
};

/**
 * Returns whether sessions should be requested from the API based on the session filter.
 */
export const shouldUseSessions = (sessionFilter: SessionFilterType): boolean => sessionFilter !== 'all';

/**
 * Derives min/max dates from optional strings (UI sends 'YYYY-MM-DD').
 */
export const getMinMaxDatesFromSelection = (selectedStartDate?: string, selectedEndDate?: string): { minDate?: Date; maxDate?: Date } => {
	const minDate = parseYmdToLocalStart(selectedStartDate);
	const maxDate = parseYmdToLocalStart(selectedEndDate);
	return { minDate, maxDate };
};

/**
 * Computes the start (Mon) and end (Sun) of the week for a given date.
 */
export const getThisWeekRange = (today: Date = new Date()): { startOfWeek: Date; endOfWeek: Date } => {
	const startOfWeek = new Date(today);
	const dayOfWeek = today.getDay();
	const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
	startOfWeek.setDate(diff);
	startOfWeek.setHours(0, 0, 0, 0);
	const endOfWeek = new Date(startOfWeek);
	endOfWeek.setDate(startOfWeek.getDate() + 6);
	endOfWeek.setHours(23, 59, 59, 999);
	return { startOfWeek, endOfWeek };
};

/**
 * Returns a date range for a given preset.
 */
export const getDateRangeForPreset = (
	preset: DatePreset,
	minDate?: Date,
	maxDate?: Date,
	today: Date = new Date()
): { dateFrom?: Date; dateTo?: Date } => {
	switch (preset) {
		case 'today': {
			const d = new Date(today);
			d.setHours(0, 0, 0, 0);
			return { dateFrom: d, dateTo: d };
		}
		case 'thisWeek': {
			const { startOfWeek, endOfWeek } = getThisWeekRange(today);
			return { dateFrom: startOfWeek, dateTo: endOfWeek };
		}
		case 'fullPeriod':
		default:
			return { dateFrom: minDate, dateTo: maxDate };
	}
};

/**
 * Normalizes an event title for filesystem-safe usage.
 */
export const normalizeEventTitle = (title?: string): string => (title ? title.replace(/[^a-zA-Z0-9]/g, '_') : 'Schedule');

/**
 * Builds a stable print filename for the schedule.
 */
export const buildPrintFilename = (eventTitle?: string, selectedStartDate?: string | Date, selectedEndDate?: string | Date): string => {
	const normalized = normalizeEventTitle(eventTitle);
	const start = typeof selectedStartDate === 'string' ? selectedStartDate : selectedStartDate?.toISOString();
	const end = typeof selectedEndDate === 'string' ? selectedEndDate : selectedEndDate?.toISOString();
	return `${normalized}_Schedule_${start ?? ''}_${end ?? ''}.pdf`;
};
